type SqliteScalar = bigint | null | number | string | Uint8Array;

export type SqliteRow = Record<string, SqliteScalar>;
export type SqliteDriverName = "better-sqlite3" | "bun:sqlite" | "node:sqlite";
export type SqliteDriverDescriptor = {
  driverName: SqliteDriverName;
  moduleSpecifier: string;
};

export type SqliteStatement = {
  all(...params: readonly SqliteScalar[]): SqliteRow[];
  get(...params: readonly SqliteScalar[]): SqliteRow | undefined;
  run(...params: readonly SqliteScalar[]): unknown;
};

export type SqliteDatabase = {
  close(): void;
  exec(sql: string): void;
  prepare(sql: string): SqliteStatement;
  readonly driverName: SqliteDriverName;
  readonly sqliteVersion: string | undefined;
};

const SQLITE_TIMEOUT_MS = 5_000;

type SqliteStatementLike = {
  all(...params: readonly unknown[]): Array<Record<string, unknown>>;
  get(...params: readonly unknown[]): Record<string, unknown> | undefined;
  run(...params: readonly unknown[]): unknown;
};
type SqliteDatabaseLike = {
  close(): void;
  exec(sql: string): void;
  prepare(sql: string): SqliteStatementLike;
};
type BetterSqliteOptions = {
  fileMustExist?: boolean;
  readonly?: boolean;
  timeout?: number;
};
type NodeSqliteOptions = {
  enableForeignKeyConstraints?: boolean;
  open?: boolean;
  readOnly?: boolean;
  timeout?: number;
};
type BunSqliteOptions = {
  create?: boolean;
  readonly?: boolean;
  readwrite?: boolean;
};
type SqliteDatabaseConstructor<TOptions> = new (
  filename: string,
  options?: TOptions,
) => SqliteDatabaseLike;

function hasRuntime(name: "Bun" | "Deno") {
  return Reflect.get(globalThis, name) !== undefined;
}

export function detectRuntimeName() {
  if (hasRuntime("Bun")) {
    return "bun" as const;
  }
  if (hasRuntime("Deno")) {
    return "deno" as const;
  }
  if (typeof process !== "undefined" && process.release.name === "node") {
    return "node" as const;
  }
  return "unknown" as const;
}

export function resolveSqliteDriverOrder(
  runtimeName = detectRuntimeName(),
): readonly SqliteDriverDescriptor[] {
  if (runtimeName === "bun") {
    return [{
      driverName: "bun:sqlite",
      moduleSpecifier: "bun:sqlite",
    }];
  }

  if (runtimeName === "deno") {
    return [{
      driverName: "node:sqlite",
      moduleSpecifier: "node:sqlite",
    }];
  }

  return [
    {
      driverName: "node:sqlite",
      moduleSpecifier: "node:sqlite",
    },
    {
      driverName: "better-sqlite3",
      moduleSpecifier: "better-sqlite3",
    },
  ];
}

export async function openSqliteDatabase(filename: string): Promise<SqliteDatabase> {
  const runtimeName = detectRuntimeName();
  const attempts: string[] = [];

  for (const driver of resolveSqliteDriverOrder(runtimeName)) {
    try {
      if (driver.driverName === "bun:sqlite") {
        const module = await importSqliteModule(driver.moduleSpecifier);
        const Database = readNamedConstructor<BunSqliteOptions>(
          module,
          "Database",
          driver.moduleSpecifier,
        );
        return wrapDatabase(
          new Database(filename, { create: true }),
          driver.driverName,
        );
      }

      if (driver.driverName === "node:sqlite") {
        const module = await importSqliteModule(driver.moduleSpecifier);
        const DatabaseSync = readNamedConstructor<NodeSqliteOptions>(
          module,
          "DatabaseSync",
          driver.moduleSpecifier,
        );
        return wrapDatabase(
          new DatabaseSync(filename, {
            enableForeignKeyConstraints: true,
            timeout: SQLITE_TIMEOUT_MS,
          }),
          driver.driverName,
        );
      }

      const module = await importSqliteModule(driver.moduleSpecifier);
      const Database = readDefaultConstructor<BetterSqliteOptions>(
        module,
        driver.moduleSpecifier,
      );
      return wrapDatabase(
        new Database(filename, {
          timeout: SQLITE_TIMEOUT_MS,
        }),
        driver.driverName,
      );
    } catch (error) {
      attempts.push(formatDriverAttempt(driver, error));
    }
  }

  throw new Error(
    `No SQLite driver could be opened for ${runtimeName}. Attempts: ${attempts.join(" | ")}`,
  );
}

function readDefaultConstructor<TOptions>(
  module: unknown,
  moduleSpecifier: string,
) {
  if (isRecord(module) && module.default !== undefined) {
    return readConstructor<TOptions>(module.default, moduleSpecifier);
  }
  return readConstructor<TOptions>(module, moduleSpecifier);
}

function readNamedConstructor<TOptions>(
  module: unknown,
  exportName: string,
  moduleSpecifier: string,
) {
  if (!isRecord(module)) {
    throw new Error(`Module "${moduleSpecifier}" did not return an object namespace`);
  }
  return readConstructor<TOptions>(module[exportName], `${moduleSpecifier}#${exportName}`);
}

function readConstructor<TOptions>(
  value: unknown,
  label: string,
) {
  if (typeof value !== "function") {
    throw new Error(`Module "${label}" did not expose a database constructor`);
  }
  return value as SqliteDatabaseConstructor<TOptions>;
}

function readSqliteVersion(database: SqliteDatabaseLike) {
  const row = database.prepare("select sqlite_version() as version").get();
  if (!isRecord(row)) {
    return undefined;
  }
  const version = row.version;
  return typeof version === "string" && version.length > 0 ? version : undefined;
}

function wrapDatabase(
  database: SqliteDatabaseLike,
  driverName: SqliteDriverName,
): SqliteDatabase {
  const sqliteVersion = readSqliteVersion(database);
  return {
    close: () => database.close(),
    driverName,
    exec: (sql) => database.exec(sql),
    prepare: (sql) => {
      const statement = database.prepare(sql);
      return {
        all: (...params) => statement.all(...params) as SqliteRow[],
        get: (...params) => statement.get(...params) as SqliteRow | undefined,
        run: (...params) => statement.run(...params),
      };
    },
    sqliteVersion,
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

async function importSqliteModule(moduleName: string) {
  return await import(moduleName);
}

function formatDriverAttempt(
  driver: SqliteDriverDescriptor,
  error: unknown,
) {
  const message = error instanceof Error ? error.message : String(error);
  return `${driver.driverName}: ${message}`;
}
